import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  CreditCard, 
  FileText, 
  MapPin, 
  Plus, 
  Trash2, 
  Check, 
  Download,
  ExternalLink,
  AlertCircle,
  RefreshCw,
  X
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Initialize Stripe - you'll need to set this in your .env
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

// Add Card Form Component
const AddCardForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError('');

    try {
      // Get setup intent from backend
      const intentResponse = await fetch(`${API_URL}/api/payments/billing/setup-intent`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });

      if (!intentResponse.ok) {
        throw new Error('Failed to initialize card setup');
      }

      const { data } = await intentResponse.json();
      const { client_secret } = data;

      // Confirm card setup with Stripe
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement)
        }
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (setupIntent.status === 'succeeded') {
        onSuccess();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-gray-300 rounded-lg bg-white">
        <CardElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': { color: '#aab7c4' }
              },
              invalid: { color: '#9e2146' }
            }
          }}
        />
      </div>
      
      {error && (
        <div className="text-red-600 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
      
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || loading}
          className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
        >
          {loading ? 'Adding...' : 'Add Card'}
        </button>
      </div>
    </form>
  );
};

const BillingPortal = () => {
  const navigate = useNavigate();
  const { user, token, getAuthHeaders } = useAuth();
  
  const [activeTab, setActiveTab] = useState('payment-methods');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [billingAddress, setBillingAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddCard, setShowAddCard] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellationInfo, setCancellationInfo] = useState(null);
  
  const [addressForm, setAddressForm] = useState({
    name: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US'
  });

  const fetchPaymentMethods = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/billing/payment-methods`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setPaymentMethods(data.data.payment_methods);
        setDefaultPaymentMethod(data.data.default_payment_method);
      }
    } catch (err) {
      console.error('Error fetching payment methods:', err);
    }
  }, [token]);

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/billing/invoices?limit=20`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setInvoices(data.data.invoices);
      }
    } catch (err) {
      console.error('Error fetching invoices:', err);
    }
  }, [token]);

  const fetchBillingAddress = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/billing/address`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success && data.data.address) {
        setBillingAddress(data.data.address);
        setAddressForm(data.data.address);
      }
    } catch (err) {
      console.error('Error fetching billing address:', err);
    }
  }, [token]);

  const fetchCancellationInfo = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/billing/cancellation-info`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setCancellationInfo(data.data);
      }
    } catch (err) {
      console.error('Error fetching cancellation info:', err);
    }
  }, [token]);

  const handleOpenCancelModal = async () => {
    await fetchCancellationInfo();
    setShowCancelModal(true);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchPaymentMethods(),
        fetchInvoices(),
        fetchBillingAddress()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchPaymentMethods, fetchInvoices, fetchBillingAddress]);

  const handleSetDefaultPayment = async (paymentMethodId) => {
    setActionLoading(true);
    setError('');
    try {
      const response = await fetch(
        `${API_URL}/api/payments/billing/payment-methods/${paymentMethodId}/set-default`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('Default payment method updated');
        fetchPaymentMethods();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(data.detail || 'Failed to update');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeletePayment = async (paymentMethodId) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) return;
    
    setActionLoading(true);
    setError('');
    try {
      const response = await fetch(
        `${API_URL}/api/payments/billing/payment-methods/${paymentMethodId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );
      const data = await response.json();
      if (data.success) {
        setSuccess('Payment method removed');
        fetchPaymentMethods();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(data.detail || 'Failed to remove');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateAddress = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/payments/billing/address`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(addressForm)
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Billing address updated');
        setBillingAddress(addressForm);
        setShowAddressForm(false);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(data.detail || 'Failed to update');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setActionLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/payments/billing/cancel-subscription`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Subscription will be cancelled at the end of your billing period');
        setShowCancelModal(false);
        setTimeout(() => setSuccess(''), 5000);
      } else {
        throw new Error(data.detail || 'Failed to cancel');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setActionLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/payments/billing/reactivate-subscription`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Subscription reactivated successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error(data.detail || 'Failed to reactivate');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCardBrandIcon = (brand) => {
    const brands = {
      visa: '💳 Visa',
      mastercard: '💳 Mastercard',
      amex: '💳 Amex',
      discover: '💳 Discover',
      default: '💳'
    };
    return brands[brand?.toLowerCase()] || brands.default;
  };

  const tabs = [
    { id: 'payment-methods', label: 'Payment Methods', icon: CreditCard },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'billing-address', label: 'Billing Address', icon: MapPin }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-grow max-w-5xl mx-auto px-6 py-8 w-full">
        <button
          onClick={() => navigate('/manage-account')}
          className="mb-6 flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Manage Account
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Billing & Payments</h1>
          <p className="text-gray-600 mt-2">Manage your payment methods, invoices, and billing information</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <Check className="w-5 h-5" />
            {success}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'text-yellow-600 border-b-2 border-yellow-500 bg-yellow-50'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Payment Methods Tab */}
            {activeTab === 'payment-methods' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-800">Your Payment Methods</h2>
                  {!showAddCard && (
                    <button
                      onClick={() => setShowAddCard(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Card
                    </button>
                  )}
                </div>

                {showAddCard && (
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h3 className="font-medium text-gray-800 mb-4">Add New Card</h3>
                    <Elements stripe={stripePromise}>
                      <AddCardForm 
                        onSuccess={() => {
                          setShowAddCard(false);
                          fetchPaymentMethods();
                          setSuccess('Card added successfully');
                          setTimeout(() => setSuccess(''), 3000);
                        }}
                        onCancel={() => setShowAddCard(false)}
                      />
                    </Elements>
                  </div>
                )}

                {paymentMethods.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No payment methods on file</p>
                    <p className="text-sm">Add a card to manage your subscription</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paymentMethods.map((pm) => (
                      <div
                        key={pm.id}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          pm.is_default ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <span className="text-2xl">{getCardBrandIcon(pm.brand)}</span>
                          <div>
                            <p className="font-medium text-gray-800">
                              {pm.brand?.charAt(0).toUpperCase() + pm.brand?.slice(1)} •••• {pm.last4}
                            </p>
                            <p className="text-sm text-gray-500">
                              Expires {pm.exp_month}/{pm.exp_year}
                            </p>
                          </div>
                          {pm.is_default && (
                            <span className="px-2 py-1 bg-yellow-200 text-yellow-800 text-xs rounded-full font-medium">
                              Default
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {!pm.is_default && (
                            <button
                              onClick={() => handleSetDefaultPayment(pm.id)}
                              disabled={actionLoading}
                              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                            >
                              Set Default
                            </button>
                          )}
                          <button
                            onClick={() => handleDeletePayment(pm.id)}
                            disabled={actionLoading || pm.is_default}
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                            title={pm.is_default ? 'Cannot delete default card' : 'Remove card'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Cancel Subscription Section */}
                {user?.stripe_subscription_id && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Subscription</h3>
                    {user?.subscription_cancel_at ? (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <p className="text-orange-800">
                          Your subscription is set to cancel on {formatDate(user.subscription_cancel_at)}
                        </p>
                        <button
                          onClick={handleReactivateSubscription}
                          disabled={actionLoading}
                          className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Reactivate Subscription
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={handleOpenCancelModal}
                        className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        Cancel Subscription
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-800">Invoice History</h2>
                
                {invoices.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No invoices yet</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Invoice</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Date</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Amount</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice) => (
                          <tr key={invoice.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <span className="font-medium text-gray-800">{invoice.number || invoice.id.slice(-8)}</span>
                            </td>
                            <td className="py-3 px-4 text-gray-600">
                              {formatDate(invoice.created)}
                            </td>
                            <td className="py-3 px-4 text-gray-800 font-medium">
                              ${invoice.amount_paid.toFixed(2)} {invoice.currency}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                invoice.status === 'paid' 
                                  ? 'bg-green-100 text-green-700'
                                  : invoice.status === 'open'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {invoice.status?.charAt(0).toUpperCase() + invoice.status?.slice(1)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {invoice.invoice_pdf && (
                                  <a
                                    href={invoice.invoice_pdf}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                                    title="Download PDF"
                                  >
                                    <Download className="w-4 h-4" />
                                  </a>
                                )}
                                {invoice.hosted_invoice_url && (
                                  <a
                                    href={invoice.hosted_invoice_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                                    title="View Invoice"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Billing Address Tab */}
            {activeTab === 'billing-address' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-800">Billing Address</h2>
                  {!showAddressForm && billingAddress && (
                    <button
                      onClick={() => setShowAddressForm(true)}
                      className="text-yellow-600 hover:text-yellow-700 font-medium"
                    >
                      Edit
                    </button>
                  )}
                </div>

                {!billingAddress && !showAddressForm ? (
                  <div className="text-center py-8">
                    <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-gray-500 mb-4">No billing address on file</p>
                    <button
                      onClick={() => setShowAddressForm(true)}
                      className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                    >
                      Add Billing Address
                    </button>
                  </div>
                ) : showAddressForm ? (
                  <form onSubmit={handleUpdateAddress} className="space-y-4 max-w-md">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                      <input
                        type="text"
                        value={addressForm.name}
                        onChange={(e) => setAddressForm({ ...addressForm, name: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                      <input
                        type="text"
                        value={addressForm.line1}
                        onChange={(e) => setAddressForm({ ...addressForm, line1: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                      <input
                        type="text"
                        value={addressForm.line2 || ''}
                        onChange={(e) => setAddressForm({ ...addressForm, line2: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          value={addressForm.city}
                          onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
                        <input
                          type="text"
                          value={addressForm.state || ''}
                          onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                        <input
                          type="text"
                          value={addressForm.postal_code}
                          onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                        <select
                          value={addressForm.country}
                          onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                          required
                        >
                          <option value="US">United States</option>
                          <option value="CA">Canada</option>
                          <option value="GB">United Kingdom</option>
                          <option value="AU">Australia</option>
                          <option value="DE">Germany</option>
                          <option value="FR">France</option>
                          <option value="JP">Japan</option>
                          <option value="SG">Singapore</option>
                          <option value="HK">Hong Kong</option>
                          <option value="TW">Taiwan</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowAddressForm(false)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={actionLoading}
                        className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
                      >
                        {actionLoading ? 'Saving...' : 'Save Address'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-medium text-gray-800">{billingAddress.name}</p>
                    <p className="text-gray-600">{billingAddress.line1}</p>
                    {billingAddress.line2 && <p className="text-gray-600">{billingAddress.line2}</p>}
                    <p className="text-gray-600">
                      {billingAddress.city}{billingAddress.state ? `, ${billingAddress.state}` : ''} {billingAddress.postal_code}
                    </p>
                    <p className="text-gray-600">{billingAddress.country}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Cancel Subscription?</h3>
            
            {cancellationInfo?.has_free_plan ? (
              <>
                <p className="text-gray-600 mb-4">
                  Your subscription will remain active until the end of your current billing period.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800">
                    After cancellation, you'll be automatically downgraded to the <strong>{cancellationInfo.free_plan_name}</strong> plan.
                  </p>
                  {cancellationInfo.free_plan_description && (
                    <p className="text-blue-600 text-sm mt-2">{cancellationInfo.free_plan_description}</p>
                  )}
                </div>
                <p className="text-gray-600 mb-6">
                  You can reactivate your subscription at any time before the cancellation date.
                </p>
              </>
            ) : (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-red-800 font-medium">
                    Warning: No free plan available
                  </p>
                  <p className="text-red-700 mt-2">
                    Your access to the service will <strong>end completely</strong> at the end of your current billing period.
                  </p>
                </div>
                <p className="text-gray-600 mb-6">
                  You can reactivate your subscription at any time before the cancellation date to avoid losing access.
                </p>
              </>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default BillingPortal;
